import React from 'react'
import PropTypes, { any } from 'prop-types'
import moment from 'moment'
import { useSelector } from 'react-redux'
import { DataGrid } from '@mui/x-data-grid'
import { Box, Grid, Typography } from '../../../../../components/mui/Core/index'
import { useTheme } from '../../../../../components/mui/Styles/index'
import LinkButton from '../../../../../components/LinkButton/link-button'
import { getCurrencyFormattedValue } from '../../../../../utils/string-utils'
import NoResultsFoundTableOverlay from '../../../../../components/NoResultsFoundTableOverlay/no-results-found-table-overlay'
import Constants from '../../../../../constants/en.json'

export function CreditInvoiceTable({
  filterBy,
  filterValue,
  handleDisplayInvoice,
  searchByOptions,
}) {
  const theme = useTheme()
  const creditInvoices = useSelector((state) => state.creditInvoice.creditInvoices)
  const isFetchingCreditInvoices = useSelector(
    (state) => state.creditInvoice.isFetchingCreditInvoices,
  )
  const selectedSearchOption = searchByOptions.filter((option) => {
    return option.id === filterBy
  })
  const filterByDescription = selectedSearchOption[0]?.text || ''
  const filterByType = selectedSearchOption[0]?.type
  const noResultsValue = filterByType === 'date' ? moment.utc(filterValue).format('L') : filterValue
  const handleCreditInvoicePdfLinkClick = (row) => {
    handleDisplayInvoice(row.invoiceHeaderID)
  }

  const rows = creditInvoices?.map((row) => ({
    id: row.index,
    invoiceHeaderID: row.invoiceHeaderID,
    creditInvoiceNumber: row.creditInvoiceNo,
    invoiceDateIssued: row.dateIssued,
    appliedToInvoice: row.appliedToInvoice,
    invoiceType: row.invoiceType,
    creditAmount: row.creditMemoCreditAmount,
    creditApplied: row.creditMemoCreditApplied,
    creditInvoiceAmount: row.creditMemoInvoiceAmount,
    invoiceAttachment: row.invoiceAttachment,
  }))

  const columns = [
    {
      field: 'creditInvoiceNumber',
      headerName: Constants.creditInvoice.CREDIT_INVOICE_NUMBER,
      flex: 0.125,
      minWidth: 150,
    },
    {
      field: 'invoiceDateIssued',
      headerName: Constants.creditInvoice.DATE_ISSUED,
      headerAlign: 'right',
      align: 'right',
      valueGetter: (value) => value,
      valueFormatter: (value) => value && moment.utc(value).format('L'),
      flex: 0.125,
      minWidth: 150,
    },
    {
      field: 'appliedToInvoice',
      headerName: Constants.creditInvoice.APPLIED_TO,
      flex: 0.125,
      minWidth: 150,
    },
    {
      field: 'invoiceType',
      headerName: Constants.creditInvoice.INVOICE_TYPE,
      flex: 0.125,
      minWidth: 150,
    },
    {
      field: 'creditAmount',
      headerName: Constants.creditInvoice.CREDIT_AMOUNT,
      headerAlign: 'right',
      align: 'right',
      valueFormatter: (value) => {
        return getCurrencyFormattedValue(value)
      },
      sortComparator: (a, b) => Number(a) - Number(b),
      flex: 0.125,
      minWidth: 150,
    },
    {
      field: 'creditApplied',
      headerName: Constants.creditInvoice.CREDIT_APPLIED,
      headerAlign: 'right',
      align: 'right',
      valueFormatter: (value) => {
        return getCurrencyFormattedValue(value)
      },
      sortComparator: (a, b) => Number(a) - Number(b),
      flex: 0.125,
      minWidth: 150,
    },
    {
      field: 'creditInvoiceAmount',
      headerName: Constants.creditInvoice.INVOICE_AMOUNT,
      headerAlign: 'right',
      align: 'right',
      valueFormatter: (value) => {
        return getCurrencyFormattedValue(value)
      },
      sortComparator: (a, b) => Number(a) - Number(b),
      flex: 0.125,
      minWidth: 150,
    },
    {
      field: 'invoiceAttachment',
      headerName: Constants.creditInvoice.ATTACHMENT,
      minWidth: 150,
      renderCell: (cellValues) => {
        const invoiceAttachment = cellValues.row.invoiceAttachment
        return (
          <LinkButton
            handleLinkClick={() => {
              handleCreditInvoicePdfLinkClick(cellValues.row)
            }}
            linkText={invoiceAttachment}
          />
        )
      },
      flex: 0.125,
    },
  ]

  const gridStyle = {
    minHeight: '160px',
    height: isFetchingCreditInvoices ? '400px' : 'auto',
    backgroundColor: theme.palette.common.white,
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: theme.palette.grey[300],
  }

  const filterModel = {
    items: [{ field: filterBy, operator: 'contains', value: filterValue }],
  }

  const noCreditInvoicesStyle = {
    position: 'absolute',
    justifyContent: 'center',
    alignItems: 'center',
    top: '50%',
    left: '50%',
    height: '50%',
    width: '50%',
  }

  return (
    <Box>
      {rows.length === 0 && !isFetchingCreditInvoices ? (
        <Grid size={{ xs: 12 }} sx={noCreditInvoicesStyle}>
          <Typography variant='message' align='center'>
            {Constants.creditInvoice.THERE_ARE_NO_CREDIT_INVOICES}
          </Typography>
        </Grid>
      ) : (
        <DataGrid
          sx={gridStyle}
          rows={rows}
          columns={columns}
          loading={isFetchingCreditInvoices}
          spacing={1}
          pageSizeOptions={[25, 50]}
          filterModel={filterModel}
          disableColumnFilter
          initialState={{
            pagination: {
              paginationModel: { pageSize: 25, page: 0 },
            },
          }}
          slotProps={{
            pagination: {
              component: 'div',
              showLastButton: true,
              showFirstButton: true,
            },
            noResultsOverlay: {
              filterByDescription,
              noResultsValue,
            },
            loadingOverlay: {
              variant: 'skeleton',
              noRowsVariant: 'skeleton',
            },
          }}
          slots={{
            noResultsOverlay: NoResultsFoundTableOverlay,
          }}
          disableSelectionOnClick
          disableRowSelectionOnClick
        />
      )}
    </Box>
  )
}

CreditInvoiceTable.propTypes = {
  filterBy: PropTypes.string.isRequired,
  filterValue: PropTypes.string.isRequired,
  handleDisplayInvoice: PropTypes.func.isRequired,
  searchByOptions: PropTypes.arrayOf(PropTypes.objectOf(any)).isRequired,
}

export default CreditInvoiceTable
